import React, { useEffect, useMemo, useState } from 'react';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import { AssetsService, DevicesService } from '../../services';

import { SelectInput } from '../SelectInput';
import { DatePickerInput } from '../DatePickerInput';
import i18next from "i18next";

import config from "config";

const required = value => (value == null ? 'Required' : undefined);

const AssetDetailReportForm = ({showPrintButton, printClicked, groupingSelected, me, initialize, initData, handleSubmit, disableDeviceSelection}) => {
  const [deviceSelected, setDeviceSelected] = useState(false);
  const [availableGroupingValues, setAvailableGroupingValues] = useState(null);
  const [grouping, setGrouping] = useState(null);
  const [availableEntities, setAvailableEntities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [beforeStart, setBeforeStart] = useState(null);
  const [acceptedTimeframe, setAcceptedTimeframe] = useState(false);
  const [timeframe, setTimeframe] = useState(null);
  const [endAtDate, setEndAtDate] = useState(null);
  const [endAtValue, setEndAtValue] = useState(null);
  const [clear, setClear] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [startAtValue, setStartAtValue] = useState(null);

  useMemo(() => {
    if (me.availableGroupings.length > 0) {
      const leafGroupings = me.availableGroupings
          .filter((grouping) => grouping.deviceGroup?.children?.length == 0)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

      setAvailableEntities(null);
      setAvailableGroupingValues(leafGroupings);
    } else {
      setAvailableGroupingValues([]);
    }

    initialize(initData);
  }, [me]);

  useEffect(() => {
    if (grouping) {
      setLoading(true);

      let requests = [];

      requests.push(AssetsService.listAllForGrouping(grouping.value).then((response) => response.assets));

      requests.push(DevicesService.listAllForGroupingFetchIdAndNameOnly(grouping.value).then((response) => response.devices));

      Promise.all(requests).then((responses) => {
        setAvailableEntities(
          responses.flatMap((entity) => entity).sort((entityA, entityB) => {
            const nameA = entityA.displayName ?? entityA.name;
            const nameB = entityB.displayName ?? entityB.name;
            if(nameA < nameB) { return -1; }
            if(nameA > nameB) { return 1; }
            return 0;
          }).map((entity) => ({value: entity.id, label: entity.displayName ?? entity.name, entityTypeToken : entity.assetType ? 'asset' : 'device'}))
        );

        setLoading(false);
      }).catch((error) => {
        console.error(error);
        // TODO handle error
        setLoading(false);
      });
    }
  }, [grouping]);

  const groupingSelectedLocal = (updatedGrouping) => {
      if (grouping && updatedGrouping.value === grouping.value) {
      // if choosing current grouping do nothing
      return;
    }
    
    groupingSelected();

    setAcceptedTimeframe(false);
    setClear(true);
    setGrouping(updatedGrouping);
  }

  const deviceSelectedLocal = (_) => {
    setDeviceSelected(true);
    setTimeframe(30);
    setClear(false);

    updateStartAtValue(startAtValue);
  }

  const updateStartAtValue = (updatedStartAtValue) => {
    if(updatedStartAtValue == null) {
      setEndAtDate(null);
      setStartDate(null);
      setAcceptedTimeframe(false);
      return
    }

    let startMilliseconds = Date.parse(updatedStartAtValue);
    let milliseconds = startMilliseconds + (timeframe * 24 * 60 * 60 * 1000)
    let endDate = new Date(milliseconds)
    // fix for daylight savings issues
    if (endDate.getHours() === 11 || endDate.getHours() === 13) {
      endDate.setHours(12)
    }

    setEndAtDate(endDate);
    setStartDate(new Date(startMilliseconds));
    setStartAtValue(updatedStartAtValue);
    
    updateEndAtSelected(endAtValue);
  };

  const updateEndAtSelected = (updatedEndAtValue) => {
    if(updatedEndAtValue == null) {
      setAcceptedTimeframe(false);
      setEndAtValue(null);
      setBeforeStart(false);
    } else {
      let endDate = Date.parse(updatedEndAtValue)
      if (! (endDate > endAtDate || endDate < startDate)) {
        setAcceptedTimeframe(true);
        setEndAtValue(updatedEndAtValue);
      } else {
        setAcceptedTimeframe(false);
        setEndAtValue(updatedEndAtValue);
        setBeforeStart(endDate < startDate);
      }
    }
  } 

  if (showPrintButton === false) {
    return (
      <div className="op-content-box filter-box" style={{marginBottom: 20}}>
        <ul className="list-inline clearfix">
          <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('filters') }</h4></li>
        </ul>
        <div className = "row">
          <div className="col-sm-3">
            <label>{ i18next.t('form-labels.location') }</label>
            <span>{initData.location.label}</span>
          </div>
          <div className="col-sm-3">
            <label>{ i18next.t('form-labels.device') }</label>
            <span>{initData.entity.label}</span>
          </div>
          <div>
            <div className="pull-left">
              <label>{ i18next.t('form-labels.date-range') }</label>
              <span>{initData.startAtDate} - {initData.endAtDate}</span>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (availableGroupingValues) {
    let hintText = beforeStart && !acceptedTimeframe ? i18next.t('messages.date-out-of-range') : i18next.t('messages.date-range-instructions', {timeframe: timeframe});
    return (
        <div className="op-content-box filter-box" style={{marginBottom: 20}}>
          <form onSubmit={handleSubmit}>
            <ul className="list-inline clearfix">
              <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('filters') }</h4></li>
            </ul>

            <div className = "row">
              <div className="col-sm-3">
                <label>{ i18next.t('form-labels.location') }</label>
                <Field
                    name={`location`}
                    options={availableGroupingValues}
                    clearable={false}
                    searchable={true}
                    component={SelectInput}
                    validate={required}
                    placeholder={ i18next.t('form-labels.select-location') }
                    onChange={groupingSelectedLocal}
                    props={{isDisabled: disableDeviceSelection}}
                />
              </div>

            {(availableEntities || disableDeviceSelection) &&
                <div className="col-sm-3">
                  <label>{ i18next.t('form-labels.asset') }</label>
                  <Field
                      name={`entity`}
                      options={availableEntities}
                      clear={clear}
                      clearable={false}
                      searchable={true}
                      placeholder={ i18next.t('form-labels.select-asset') }
                      component={SelectInput}
                      validate={required}
                      onChange={deviceSelectedLocal}
                      props={{isDisabled: disableDeviceSelection}}
                  />
                </div>
              }

              {(deviceSelected || disableDeviceSelection) &&
                  <div>
                    <div className="align-date-picker pull-left">
                      <label>{ i18next.t('form-labels.date-range') }</label>
                        <Field
                            name={`startAtDate`}
                            component={DatePickerInput}
                            onDayChange={updateStartAtValue}
                            isDisabled={disableDeviceSelection}
                        />
                    </div>
                    <div className="dash pull-left">-</div>
                    <div className="align-date-picker pull-left">
                      <label className="date-end" style={{visibility: 'hidden'}}>{ i18next.t('form-labels.date-range-end') }</label>
                        <Field
                            name={`endAtDate`}
                            component={DatePickerInput}
                            onDayChange={updateEndAtSelected}
                            minDate={startDate}
                            maxDate={endAtDate}
                            isDisabled={disableDeviceSelection}
                        />
                    </div>
                  </div>
                }

                {
                  acceptedTimeframe && showPrintButton &&
                  <div className="pull-right print-image tooltip">
                    <a>
                      <img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/print_icon.svg`}
                          className="print-icon"
                          onClick={printClicked}
                          alt={ i18next.t('print-report') } />
                    </a>
                    <span className="tooltiptext print-image">{ i18next.t('print') }</span>
                  </div>
                }
                {
                  acceptedTimeframe &&
                  <div className="pull-left">
                      <label style={{visibility: 'hidden'}}>{ i18next.t('form-labels.run-report') }</label>
                      <ul className="list-inline clearfix">
                        <li className="pull-left" style={{marginLeft: '15px'}}><button type="submit" className="btn btn-sm btn-default pull-right" >{ i18next.t('form-labels.run-report') }</button></li>
                      </ul>
                  </div>
                }
            </div>

            {
              endAtDate && !acceptedTimeframe && endAtValue && !clear &&
              <div className = "hint">
                <p> {hintText} </p>
              </div>
            }
          </form>
          {loading && <div className="loader">{ i18next.t('loading') }</div>}
        </div>
    );
  } else {
    return (
        <div className="loader">{ i18next.t('loading') }</div>
    );
  }
}


export default connect(state => ({ me: state.user.me }))(reduxForm({ form: 'AssetDetailReportForm' })(AssetDetailReportForm));
